import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, Loader2, CheckCircle, XCircle, Clock,
  AlertTriangle, Send, Edit2, Save, X, ChevronDown, ChevronRight, Shield, CreditCard, Copy,
  TrendingUp, AlertOctagon, ThumbsUp, RefreshCw, Printer, AlertCircle,
  FileSpreadsheet, Upload, Brain
} from "lucide-react";
import { OfferAISummary } from "@/components/offer/OfferAISummary";
import { ScenarioComparisonMatrix } from "@/components/offer/ScenarioComparisonMatrix";
import { MaintenanceYearlyChart } from "@/components/offer/MaintenanceYearlyChart";
import { ApprovalHistory } from "@/components/offer/ApprovalHistory";
import { ItValidateModal } from "@/components/offer/ItValidateModal";
import { CfoApproveModal } from "@/components/offer/CfoApproveModal";
import { SilentAdminModal } from "@/components/offer/SilentAdminModal";
import { CfoUpdateModal } from "@/components/offer/CfoUpdateModal";
import OfferCliente360 from "@/components/offer/OfferCliente360";
import { OfferImprovements } from "@/components/offer/OfferImprovements";
import LinkLicitacionModal from "@/components/offer/LinkLicitacionModal";
import { useOfferAiAnalysis } from "@/hooks/useOfferAiAnalysis";
import { useCostSections } from "@/hooks/useCostSections";
import { CONTRACT_TYPES, contractTypeLabel } from "@/lib/offerConstants";
import { toast } from "sonner";

// ── Role helpers ────────────────────────────────────────────────────────────
const IT_VALIDATORS  = ["asanchezm@valorizasm.com", "dredondo@valorizasm.com"];
const CFO_EMAILS     = ["gferreresd@valorizasm.com"];
const isItValidator  = (email) => IT_VALIDATORS.includes(email);
const isCfo          = (user) => user?.role === "cfo" || CFO_EMAILS.includes(user?.email || "");

// ── Status config ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  draft:                  { label: "Borrador",                    cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400",  icon: Clock },
  in_review:              { label: "Borrador",                    cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400",  icon: Clock },
  pending_validation:     { label: "Pendiente validación TIC",   cls: "bg-amber-100 text-amber-700",  dot: "bg-amber-400", icon: AlertTriangle },
  pending_it_validation:  { label: "Pendiente validación TIC",   cls: "bg-amber-100 text-amber-700",  dot: "bg-amber-400", icon: AlertTriangle },
  pending_cfo_approval:   { label: "Pendiente aprobación CFO",   cls: "bg-blue-100 text-blue-700",    dot: "bg-blue-500",  icon: CreditCard },
  approved:               { label: "Aprobada CFO ✓",             cls: "bg-green-100 text-green-700",  dot: "bg-green-500", icon: CheckCircle },
  approved_with_conditions: { label: "Aprobada con condiciones ⚠", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500", icon: AlertTriangle },
  rejected:               { label: "Rechazada",                  cls: "bg-red-100 text-red-700",      dot: "bg-red-500",   icon: XCircle },
};

const CATEG_STYLE = {
  OPEX:  { bg: "bg-teal-700",   text: "text-white", badge: "bg-teal-100 text-teal-800" },
  CAPEX: { bg: "bg-[#007934]",  text: "text-white", badge: "bg-green-100 text-green-800" },
};

/**
 * Construye la data plurianual del coste de mantenimiento tecnología para el gráfico.
 *
 * Reconstruye la serie a partir de:
 *   - offer.costes_mantenimiento_by_year (base — proveedor externo por año)
 *   - offer.cost_sections filtradas por OPEX + subcategory ~ "Horas internas IT"
 *     (esas son las horas RECURRENTES inyectadas por el análisis Pando)
 *
 * Normaliza el campo legacy `moba` → `distromel` (misma columna del gráfico).
 * Recalcula `total = distromel + horas_internas` para que la línea roja refleje SIEMPRE
 * la suma real, incluyendo las horas internas inyectadas post-análisis Pando.
 */
function buildMaintenanceYearlyData(offer) {
  const base = offer?.costes_mantenimiento_by_year;
  if (!Array.isArray(base) || base.length === 0) return null;

  // Horas internas OPEX RECURRENTES inyectadas por análisis Pando
  // (SOLO auto_internal_hours=true — las secciones seed OPEX 'Horas Internas'
  //  ya están reflejadas en row.horas_internas para evitar doble-conteo).
  const opexHorasInternasAnnualAI = (offer?.cost_sections || [])
    .filter(
      (s) =>
        (s.category || "").toUpperCase() === "OPEX" &&
        s.auto_internal_hours === true
    )
    .reduce((acc, s) => {
      const sub = (s.items || []).reduce(
        (a, it) => a + (Number(it.cantidad || 0) * Number(it.precio_unitario || 0)),
        0
      );
      return acc + sub * (s.years || 1);
    }, 0);

  const hasAIHours = opexHorasInternasAnnualAI > 0;

  const series = base.map((row) => {
    // Campo proveedor externo: normaliza legacy `moba`, `provider`, `external`
    const distromel = Number(
      row.distromel ?? row.moba ?? row.provider ?? row.external ?? 0
    );
    // Si el seed ya trae horas_internas explícitas, respétalo tal cual;
    // si no, usa las inyectadas por Pando (evita doble-conteo).
    const seedHoras = Number(row.horas_internas || 0);
    const horas_internas = seedHoras > 0 ? seedHoras : Number(opexHorasInternasAnnualAI || 0);
    const total = distromel + horas_internas;
    return {
      ...row,
      distromel,
      horas_internas,
      total,
    };
  });

  // Si todos los años suman 0 (offer con esquema plurianual alternativo tipo
  // la-linea-grua-2026 que usa opex_total/ebitda/ingresos), ocultar el chart.
  if (series.every((r) => r.distromel + r.horas_internas === 0)) return null;

  // Anexa metadata Pando (no rompe el shape · MaintenanceYearlyChart mapea sólo `data`)
  const ai = offer?.ai_analysis || {};
  series.__meta = {
    aiRecomputed: hasAIHours,
    aiGeneratedAt: ai.generated_at || null,
    aiModel: ai.model || ai.synthesis_model || null,
    aiCostUsd: typeof ai.cost_usd === "number" ? ai.cost_usd : null,
    aiSessionId: ai.session_id || ai.job_id || null,
    aiLicitacionId: ai.licitacion_id || null,
  };
  return series;
}

const fmt = (n) => Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── History action labels ────────────────────────────────────────────────────
const ACTION_LABELS = {
  validation_requested: "Solicitud enviada a TIC",
  approve:              "Validado TIC → escalado a CFO",
  reject:               "Rechazado por TIC",
  cfo_approve:          "Aprobado por CFO",
  cfo_reject:           "Rechazado por CFO",
};

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [offer, setOffer]                   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [editMeta, setEditMeta]             = useState(false);
  const [metaForm, setMetaForm]             = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  const [savingMeta, setSavingMeta]         = useState(false);

  // Action states
  const [requesting, setRequesting]         = useState(false);
  const [showItModal, setShowItModal]       = useState(false);
  const [itForm, setItForm]                 = useState({ action: "approve", comments: "", conditions: "" });
  const [validating, setValidating]         = useState(false);
  const [showCfoModal, setShowCfoModal]     = useState(false);
  const [cfoForm, setCfoForm]               = useState({ action: "approve", comments: "", reject_category: null, conditions: "" });
  const [cfoApproving, setCfoApproving]     = useState(false);
  const [showCfoUpdateModal, setShowCfoUpdateModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);  // Iter223

  // AI analysis (model + flags UI · hook maneja el estado y polling)
  const [aiModel, setAiModel]               = useState("claude-sonnet-4-6");
  const [aiModelsCatalog, setAiModelsCatalog] = useState([]);
  const [includeInternalHours, setIncludeInternalHours] = useState(true);

  // Excel import
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [excelImportFile, setExcelImportFile] = useState(null);
  const [importingExcel, setImportingExcel]   = useState(false);
  const [importResult, setImportResult]       = useState(null);
  const excelInputRef = useRef(null);

  // ── Scenarios (CHIP2CHIP / DISTROMEL / Híbrido) ──────────────────────────
  const [scenarios, setScenarios]             = useState(null);
  const [activeScenario, setActiveScenario]   = useState(null);
  const [scenarioSwitching, setScenarioSwitching] = useState(false);
  const [showComparison, setShowComparison]   = useState(false);

  // Hooks extraídos — sections (CAPEX/OPEX) y AI analysis (polling asíncrono)
  const { sections, setSections, saving, saveSections } = useCostSections(id, []);
  const { aiAnalysis, setAiAnalysis, analyzingAi, runAiAnalysis } = useOfferAiAnalysis(id, {
    aiModel,
    includeInternalHours,
    onOfferRefresh: setOffer,
    onSectionsRefresh: setSections,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/offers/${id}`);
      setOffer(data);
      setSections(data.cost_sections || []);
      setAiAnalysis(data.ai_analysis || null);
      setActiveScenario(data.active_scenario || null);
      setMetaForm({
        title: data.title, licitacion: data.licitacion || "",
        contract_type: (data.contract_type || "SER").toUpperCase(),
        contract_type_other: data.contract_type_other || "",
        municipality: data.municipality || "",
        deadline: data.deadline || "", responsible_business: data.responsible_business || "",
        responsible_business_email: data.responsible_business_email || "",
        description: data.description || "",
        dev_hours: data.dev_hours ?? "",
        dev_rate: data.dev_rate ?? "",
      });
      const expanded = {};
      (data.cost_sections || []).slice(0, 2).forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
      // Lista de escenarios disponibles (solo si el doc los tiene definidos)
      try {
        const sc = await axios.get(`${API}/offers/${id}/scenarios`);
        setScenarios(sc.data.scenarios || {});
        setActiveScenario(sc.data.active_scenario || null);
      } catch { setScenarios(null); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const switchScenario = async (key) => {
    if (scenarioSwitching || key === activeScenario) return;
    setScenarioSwitching(true);
    try {
      await axios.post(`${API}/offers/${id}/set-scenario`, { scenario: key });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo cambiar de escenario");
    } finally {
      setScenarioSwitching(false);
    }
  };

  // ── Catálogo de modelos Pando (Sonnet/Opus/Haiku) ────────────────────────────
  useEffect(() => {
    axios.get(`${API}/ai/models`)
      .then(r => setAiModelsCatalog(r.data?.models || []))
      .catch(() => {});
  }, []);

  // ── CAPEX/OPEX editing ────────────────────────────────────────────────────
  // (saveSections viene del hook useCostSections; ya no hace falta declararlo aquí)

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      // Iter279 · dev_hours/dev_rate llegan como "" cuando el campo está
      // vacío en el input; el backend los tipa como int/float y rechaza ""
      // con 422 — antes esto hacía fallar el guardado ENTERO en silencio
      // (también para el resto de campos del formulario, ej. Resp. Negocio).
      const payload = {
        ...metaForm,
        dev_hours: metaForm.dev_hours === "" || metaForm.dev_hours == null ? null : Number(metaForm.dev_hours),
        dev_rate: metaForm.dev_rate === "" || metaForm.dev_rate == null ? null : Number(metaForm.dev_rate),
      };
      const { data } = await axios.put(`${API}/offers/${id}`, payload);
      setOffer(data);
      setEditMeta(false);
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "No se pudieron guardar los cambios. Revisa los campos e inténtalo de nuevo.");
    }
    finally { setSavingMeta(false); }
  };

  const addSection = (category) => {
    const newSec = { id: `${Date.now()}`, category, subcategory: category === "OPEX" ? "Mantenimiento" : "SER", section_name: "Nueva sección", items: [] };
    const updated = [...sections, newSec];
    setSections(updated);
    setExpandedSections(prev => ({ ...prev, [newSec.id]: true }));
    saveSections(updated);
  };

  const updateSection = (secId, field, value) =>
    setSections(prev => prev.map(s => s.id === secId ? { ...s, [field]: value } : s));

  const removeSection = (secId) => {
    const updated = sections.filter(s => s.id !== secId);
    setSections(updated);
    saveSections(updated);
  };

  const addItem = (secId) => {
    const updated = sections.map(s => s.id === secId ? {
      ...s, items: [...(s.items || []), { id: `${Date.now()}`, concepto: "", cantidad: 1, precio_unitario: 0, nota: "" }]
    } : s);
    setSections(updated);
  };

  const updateItem = (secId, itemId, field, value) =>
    setSections(prev => prev.map(s => s.id === secId ? {
      ...s, items: s.items.map(i => i.id === itemId
        ? { ...i, [field]: field === "concepto" || field === "nota" ? value : (parseFloat(value) || 0) } : i)
    } : s));

  const removeItem = (secId, itemId) => {
    const updated = sections.map(s => s.id === secId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s);
    setSections(updated);
    saveSections(updated);
  };

  const saveSection = () => saveSections(sections);

  // ── Approval actions ──────────────────────────────────────────────────────
  const requestValidation = async () => {
    setRequesting(true);
    try {
      await saveSections(sections);
      const { data } = await axios.post(`${API}/offers/${id}/request-validation`);
      setOffer(data);
    } catch (e) { alert(e.response?.data?.detail || "Error solicitando validación"); }
    finally { setRequesting(false); }
  };

  const handleItValidate = async () => {
    if (itForm.action === "reject" && !itForm.comments.trim()) {
      alert("Debes añadir un comentario al rechazar"); return;
    }
    if (itForm.action === "approve_with_conditions" && !(itForm.conditions || "").trim()) {
      alert("Debes indicar las condiciones exigidas"); return;
    }
    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/offers/${id}/validate`, itForm);
      setOffer(data);
      setSections(data.cost_sections || []);
      setShowItModal(false);
    } catch (e) { alert(e.response?.data?.detail || "Error al validar"); }
    finally { setValidating(false); }
  };

  const handleCfoApprove = async () => {
    if (cfoForm.action === "reject" && !cfoForm.comments.trim()) {
      alert("Debes añadir un comentario al rechazar"); return;
    }
    if (cfoForm.action === "reject" && !cfoForm.reject_category) {
      alert("Debes seleccionar una categoría de rechazo"); return;
    }
    if (cfoForm.action === "approve_with_conditions" && !(cfoForm.conditions || "").trim()) {
      alert("Debes indicar las condiciones exigidas"); return;
    }
    setCfoApproving(true);
    try {
      const { data } = await axios.post(`${API}/offers/${id}/cfo-approve`, cfoForm);
      setOffer(data);
      setSections(data.cost_sections || []);
      setShowCfoModal(false);
    } catch (e) { alert(e.response?.data?.detail || "Error en la aprobación CFO"); }
    finally { setCfoApproving(false); }
  };

  // Silent admin approve (dredondo only) — atajo TIC + CFO sin envío de emails
  const [silentApproving, setSilentApproving] = useState(false);
  const [showSilentModal, setShowSilentModal] = useState(false);

  const handleSilentAdminApprove = async (reason) => {
    setSilentApproving(true);
    try {
      await saveSections(sections);   // persistir cambios pendientes antes de aprobar
      const { data } = await axios.post(
        `${API}/offers/${id}/silent-admin-approve`,
        { reason: reason || "modificación" }
      );
      setOffer(data);
      setSections(data.cost_sections || []);
      setShowSilentModal(false);
      toast.success(
        `Oferta aprobada silenciosamente — sin envío de emails`,
        {
          description: `TIC ✓ + CFO ✓ registrado en histórico como silent_admin_update · motivo: ${reason}`,
          duration: 8000,
          action: {
            label: "Ver histórico",
            onClick: () => {
              const el = document.querySelector('[data-testid="approval-history-section"]');
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.classList.add("ring-2","ring-indigo-400","rounded-xl");
                setTimeout(() => el.classList.remove("ring-2","ring-indigo-400","rounded-xl"), 2500);
              }
            },
          },
        }
      );
    } catch (e) {
      toast.error("Error en el atajo admin", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setSilentApproving(false);
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleExcelImportInOffer = async () => {
    if (!excelImportFile) return;
    setImportingExcel(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", excelImportFile);
      const { data } = await axios.post(`${API}/offers/${id}/import-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setOffer(data.offer);
      setSections(data.offer.cost_sections || []);
      setImportResult({ resumen: data.resumen_importacion, sections_count: data.sections_count });
    } catch (e) {
      alert(`Error al importar: ${e.response?.data?.detail || e.message}`);
    } finally {
      setImportingExcel(false);
    }
  };

  // ── Totals (years multiplier for CAPEX) ──────────────────────────────────
  const sectionBaseTotal = (sec) => (sec.items || []).reduce((s, i) => s + (i.cantidad || 0) * (i.precio_unitario || 0), 0);
  const sectionTotal = (sec) => {
    const base = sectionBaseTotal(sec);
    return sec.category === "CAPEX" ? base * Math.max(sec.years || 1, 1) : base;
  };
  const opexTotal  = sections.filter(s => s.category === "OPEX").reduce((s, sec) => s + sectionBaseTotal(sec), 0);
  const capexTotal = sections.filter(s => s.category === "CAPEX").reduce((s, sec) => s + sectionTotal(sec), 0);
  const grandTotal = opexTotal + capexTotal;
  // Si el OPEX viene desglosado por anualidad completa ("Anualidad 1", "Anualidad
  // 2"...), opexTotal ya es el OPEX de TODO el contrato, no de 1 año — la etiqueta
  // "OPEX / año" debe mostrar el promedio, no la suma. El "TOTAL TIC" no cambia.
  const opexSections = sections.filter(s => s.category === "OPEX");
  const opexIsMultiYear = opexSections.length >= 2 &&
    opexSections.every(s => /^anualidad\s*\d+$/i.test((s.section_name || "").trim()));
  const opexAnnualLabel = opexIsMultiYear ? opexTotal / opexSections.length : opexTotal;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-[#007934]" /></div>;
  if (!offer)  return <div className="text-center py-16 text-gray-400">Oferta no encontrada</div>;

  const st      = STATUS_MAP[offer.status] || STATUS_MAP.draft;
  const userEmail = user?.email || "";
  const isAdmin   = user?.role === "admin";
  const status  = offer.status;

  return (
    <div className="space-y-5 w-full" data-testid="offer-detail">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate("/offers")} className="mt-1 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          {editMeta ? (
            <div className="space-y-3">
              <input value={metaForm.title} onChange={e => setMetaForm({...metaForm, title: e.target.value})}
                className="text-xl font-bold w-full border-b border-[#007934] outline-none py-1 font-['Outfit']" />
              <div className="flex gap-2">
                <button onClick={saveMeta} disabled={savingMeta} className="bg-[#007934] text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                  <Save size={12} />{savingMeta ? "Guardando…" : "Guardar"}
                </button>
                <button onClick={() => setEditMeta(false)} className="border border-gray-200 px-3 py-1.5 rounded-lg text-xs text-gray-600">Cancelar</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">{offer.title}</h1>
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{offer.licitacion}{offer.municipality ? ` · ${offer.municipality}` : ""}</p>
                {(offer.tic_conditions || offer.cfo_conditions) && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="offer-active-conditions">
                    <b>⚠ Condiciones exigidas:</b> {offer.cfo_conditions || offer.tic_conditions}
                  </div>
                )}
                {offer.reopened_reason === "material_economic_change" && offer.status === "pending_it_validation" && (
                  <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700" data-testid="offer-reopened-banner">
                    <b>↺ Reabierta automáticamente:</b> la aprobación anterior ({offer.reopened_from_status}) quedó invalidada por un cambio económico material. Debe revalidarse.
                  </div>
                )}
              </div>
              <button onClick={() => setEditMeta(true)} className="text-gray-300 hover:text-[#007934] p-2 rounded-lg hover:bg-gray-50 transition-all"><Edit2 size={15} /></button>
              <button
                onClick={async () => {
                  try {
                    const { data } = await axios.get(`${API}/offers/${offer.id}/export-payload`);
                    const json = JSON.stringify(data, null, 2);
                    // Preferimos writeText; si el browser no permite (contexto no-secure), abrimos ventana con textarea
                    try {
                      await navigator.clipboard.writeText(json);
                      toast.success("Payload copiado al portapapeles", {
                        description: `${data.cost_sections?.length} secciones · pega el JSON en "Importar JSON" del entorno destino`,
                      });
                    } catch {
                      const w = window.open("", "_blank", "width=800,height=600");
                      if (w) {
                        w.document.body.innerHTML = `<pre style="font-family:monospace;font-size:11px;padding:12px;white-space:pre-wrap">${json.replace(/</g, "&lt;")}</pre>`;
                        toast.info("Payload abierto en pestaña nueva — copia manualmente");
                      } else {
                        toast.error("No se pudo copiar ni abrir ventana");
                      }
                    }
                  } catch (e) {
                    toast.error("Error exportando payload", { description: e.response?.data?.detail || e.message });
                  }
                }}
                title="Copia el JSON de esta oferta al portapapeles para importarla en otro entorno (ej. Producción)"
                data-testid="offer-copy-payload-btn"
                className="text-gray-400 hover:text-[#007934] p-2 rounded-lg hover:bg-gray-50 transition-all"
              >
                <Copy size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scenario toggle — sólo si la oferta tiene escenarios definidos */}
      {scenarios && Object.keys(scenarios).length > 1 && (
        <div data-testid="offer-scenario-toggle" className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Escenario económico</p>
              <p className="text-xs text-gray-500 mt-0.5">Alterna entre las propuestas guardadas (CAPEX/OPEX + análisis Pando se actualizan al instante).</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(scenarios).map(([key, sc]) => {
                const isActive = key === activeScenario;
                return (
                  <button
                    key={key}
                    data-testid={`offer-scenario-btn-${key}`}
                    onClick={() => switchScenario(key)}
                    disabled={scenarioSwitching}
                    className={`px-4 py-2 rounded-lg border transition-all text-left ${
                      isActive
                        ? "bg-[#007934] text-white border-[#007934] shadow-md"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#007934] hover:bg-green-50"
                    } ${scenarioSwitching ? "opacity-50 cursor-wait" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      {isActive && <CheckCircle size={14} />}
                      <span className="text-xs font-semibold">{sc.label}</span>
                    </div>
                    <div className={`text-[10px] mt-1 ${isActive ? "text-green-100" : "text-gray-500"}`}>
                      CAPEX {fmt(sc.capex_total)} € · OPEX {fmt(sc.opex_total)} €/año
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[11px] text-gray-500">¿Necesitas ver los 3 escenarios en paralelo para el comité?</p>
            <button
              data-testid="toggle-comparison-btn"
              onClick={() => setShowComparison((v) => !v)}
              className="text-xs font-semibold text-[#007934] hover:underline"
            >
              {showComparison ? "Ocultar comparador" : "Ver comparador 3×N →"}
            </button>
          </div>
        </div>
      )}

      {/* Comparator matrix */}
      {showComparison && scenarios && Object.keys(scenarios).length > 1 && (
        <ScenarioComparisonMatrix offerId={id} activeScenario={activeScenario} />
      )}

      {/* Meta info + Totals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          {editMeta ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[["licitacion","Licitación/Expediente"],["municipality","Municipio"],["deadline","Deadline"],["responsible_business","Resp. Negocio"],["responsible_business_email","Email negocio"]].map(([f, l]) => (
                <div key={f}>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">{l}</label>
                  <input value={metaForm[f] || ""} onChange={e => setMetaForm({...metaForm, [f]: e.target.value})}
                    type={f === "deadline" ? "date" : f.includes("email") ? "email" : "text"}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
              ))}
              {/* Iter221 · Tipo de contrato como dropdown */}
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Tipo de contrato</label>
                <select
                  value={metaForm.contract_type || "SER"}
                  onChange={e => setMetaForm({...metaForm, contract_type: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[#007934]"
                  data-testid="meta-contract-type-select"
                >
                  {CONTRACT_TYPES.map(ct => (
                    <option key={ct.code} value={ct.code}>{ct.code} — {ct.label}</option>
                  ))}
                  {/* Legacy: si el tipo actual no está en el catálogo nuevo lo dejamos elegible */}
                  {metaForm.contract_type && !CONTRACT_TYPES.some(ct => ct.code === metaForm.contract_type) && (
                    <option value={metaForm.contract_type}>{metaForm.contract_type} — (legacy)</option>
                  )}
                </select>
                {metaForm.contract_type === "OTROS" && (
                  <input
                    value={metaForm.contract_type_other || ""}
                    onChange={e => setMetaForm({...metaForm, contract_type_other: e.target.value})}
                    placeholder="Describe el tipo de contrato"
                    className="mt-2 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#007934]"
                    data-testid="meta-contract-type-other-input"
                  />
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Descripción</label>
                <textarea rows={2} value={metaForm.description || ""} onChange={e => setMetaForm({...metaForm, description: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[#007934] resize-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Horas de Desarrollo estimadas</label>
                <input type="number" min="0" placeholder="ej. 320" value={metaForm.dev_hours ?? ""} onChange={e => setMetaForm({...metaForm, dev_hours: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Tarifa empleados (€/h)</label>
                <input type="number" min="0" step="0.5" placeholder="ej. 65" value={metaForm.dev_rate ?? ""} onChange={e => setMetaForm({...metaForm, dev_rate: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
              {[
                ["Licitación", offer.licitacion], ["Tipo contrato", contractTypeLabel(offer.contract_type, offer.contract_type_other)],
                ["Municipio", offer.municipality], ["Deadline", offer.deadline],
                ["Resp. Negocio", offer.responsible_business], ["Resp. TIC", offer.responsible_tech],
                ["H. Desarrollo", offer.dev_hours ? `${offer.dev_hours} h` : null],
                ["Tarifa", offer.dev_rate ? `${offer.dev_rate} €/h` : null],
              ].map(([l, v]) => v ? (
                <div key={l} className="flex items-start gap-2 py-1">
                  <span className="text-xs text-gray-400 w-28 flex-shrink-0 mt-0.5">{l}</span>
                  <span className="font-medium text-gray-900 text-xs">{v}</span>
                </div>
              ) : null)}
              {offer.description && (
                <div className="col-span-2 pt-2 border-t border-gray-50">
                  <p className="text-xs text-gray-400 mb-1">Descripción</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{offer.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="offer-totals">
          <h3 className="font-bold text-gray-900 font-['Outfit'] mb-4 text-sm">Resumen Económico TIC</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-teal-700 font-medium flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-teal-500"/>OPEX / año{opexIsMultiYear ? " (promedio)" : ""}</span>
              <span className="font-bold text-teal-700">{fmt(opexAnnualLabel)} €</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-50">
              <span className="text-sm text-[#007934] font-medium flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#007934]"/>CAPEX</span>
              <span className="font-bold text-[#007934]">{fmt(capexTotal)} €</span>
            </div>
            <div className="flex justify-between items-center py-2 bg-gray-50 rounded-lg px-3">
              <span className="text-sm font-bold text-gray-900">TOTAL TIC</span>
              <span className="font-bold text-gray-900 text-lg">{fmt(grandTotal)} €</span>
            </div>
          </div>
          {saving && <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Guardando…</p>}
        </div>
      </div>

      {/* Iter223 · Cliente 360º · trazabilidad de la oferta */}
      <OfferCliente360 offerId={id} onOpenLinkModal={() => setShowLinkModal(true)} />

      {/* ── Approval Flow Panel ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="approval-section">
        {/* Pipeline steps */}
        <div className="mb-5">
          <h3 className="font-bold text-gray-900 font-['Outfit'] text-sm mb-4">Flujo de Aprobación</h3>
          <div className="flex items-center gap-0">
            {[
              { key: "draft",                label: "Borrador",         icon: Clock },
              { key: "pending_it_validation",label: "Validación TIC",   icon: Shield },
              { key: "pending_cfo_approval", label: "Aprobación CFO",   icon: CreditCard },
              { key: "approved",             label: "Aprobada",         icon: CheckCircle },
            ].map((step, idx, arr) => {
              const steps = ["draft","in_review","pending_it_validation","pending_validation","pending_cfo_approval","approved","approved_with_conditions","rejected"];
              const currentIdx = steps.indexOf(status);
              const stepIdx = steps.indexOf(step.key);
              const isDone    = currentIdx > stepIdx || status === "approved" || status === "approved_with_conditions";
              const isCurrent = step.key === status || step.key === "draft" && status === "in_review" || (step.key === "pending_it_validation" && status === "pending_validation") || (step.key === "approved" && status === "approved_with_conditions");
              const isRejected = status === "rejected";
              const Icon = step.icon;
              return (
                <React.Fragment key={step.key}>
                  <div className={`flex flex-col items-center flex-1 ${idx > 0 ? "mt-0" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                      ${isRejected && isCurrent ? "bg-red-100 text-red-600 ring-2 ring-red-400"
                        : isCurrent  ? "bg-[#007934] text-white ring-2 ring-[#007934] ring-offset-2"
                        : isDone     ? "bg-[#007934] text-white"
                        : "bg-gray-100 text-gray-400"}`}>
                      <Icon size={14} />
                    </div>
                    <p className={`text-[10px] mt-1.5 text-center leading-tight font-medium
                      ${isCurrent ? "text-[#007934]" : isDone ? "text-gray-700" : "text-gray-400"}`}>
                      {step.label}
                    </p>
                  </div>
                  {idx < arr.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-5 ${isDone ? "bg-[#007934]" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {status === "rejected" && (
            <div className="mt-2 flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              <XCircle size={13} /> Oferta rechazada
            </div>
          )}
        </div>

        {/* Current status + actions */}
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm ${st.cls}`}>
            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
            <span className="font-medium">{st.label}</span>
            {offer.validated_by && <span className="text-xs opacity-70 ml-2">— por {offer.validated_by}</span>}
            {offer.cfo_approved_by && <span className="text-xs opacity-70 ml-2">— por {offer.cfo_approved_by}</span>}
          </div>

          <div className="flex items-center gap-2">
            {/* Step 1: Request IT validation */}
            {!["pending_it_validation","pending_validation","pending_cfo_approval","approved","rejected","desestimada"].includes(status) && (user?.role === "offer_manager" || isAdmin) && (
              <button onClick={requestValidation} disabled={requesting}
                className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-600 disabled:opacity-60 shadow-sm"
                data-testid="request-validation-btn">
                {requesting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {requesting ? "Enviando…" : "Solicitar validación TIC"}
              </button>
            )}
            {/* Step 2: IT validate */}
            {(status === "pending_it_validation" || status === "pending_validation") && (isItValidator(userEmail) || isAdmin) && (
              <button onClick={() => setShowItModal(true)}
                className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#006528] shadow-sm"
                data-testid="it-validate-btn">
                <Shield size={14} /> Validar TIC
              </button>
            )}
            {/* Step 3: CFO approve */}
            {status === "pending_cfo_approval" && (isCfo(user) || isAdmin) && (
              <button onClick={() => setShowCfoModal(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-sm"
                data-testid="cfo-approve-btn">
                <CreditCard size={14} /> Aprobación CFO
              </button>
            )}
            {/* Iter215 · Reenvío CFO con diff IA — visible si ya existe algún email previo al CFO */}
            {(offer.cfo_email_snapshots?.length || 0) > 0 &&
              !["rejected", "desestimada"].includes(status) &&
              (isCfo(user) || isAdmin || isItValidator(userEmail)) && (
              <button
                onClick={() => setShowCfoUpdateModal(true)}
                className="flex items-center gap-2 bg-white border-2 border-indigo-500 text-indigo-700 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-indigo-50 shadow-sm"
                data-testid="cfo-update-btn"
                title="Genera con IA un email al CFO informando de los cambios desde el último envío"
              >
                <Send size={13} /> Actualizar CFO (IA)
                <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-mono">
                  {offer.cfo_email_snapshots.length}
                </span>
              </button>
            )}
            {/* Silent admin approve (dredondo only) — atajo TIC + CFO sin envío de emails.
                Visible SIEMPRE (incluso en 'approved' para re-aprobar tras modificaciones)
                salvo cuando la oferta está desestimada o rechazada. */}
            {(userEmail || "").toLowerCase() === "dredondo@valorizasm.com" && !["rejected","desestimada"].includes(status) && (
              <button
                onClick={() => setShowSilentModal(true)}
                disabled={silentApproving}
                data-testid="silent-admin-approve-btn"
                title="Solo dredondo · Marca como VALIDADO TIC + APROBADO CFO sin enviar emails. Ideal para modificaciones sobre ofertas ya aprobadas."
                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 py-2 rounded-xl text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 shadow-sm border border-purple-300"
              >
                {silentApproving ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
                {silentApproving ? "Aprobando…" : status === "approved" ? "🤫 Re-aprobar en silencio" : "🤫 Aprobar en silencio (admin)"}
              </button>
            )}
          </div>
        </div>

        {/* Comments display */}
        {offer.validation_comments && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 font-medium mb-1">Comentarios TIC</p>
            <p className="text-sm text-gray-700">{offer.validation_comments}</p>
          </div>
        )}
        {offer.cfo_comments && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-500 font-medium mb-1">Comentarios CFO</p>
            <p className="text-sm text-gray-700">{offer.cfo_comments}</p>
          </div>
        )}

        {/* ── IT Validation Modal ─────────────────────────────────────────── */}
        <ItValidateModal
          open={showItModal}
          form={itForm}
          onFormChange={setItForm}
          onCancel={() => setShowItModal(false)}
          onConfirm={handleItValidate}
          loading={validating}
        />

        {/* ── CFO Approval Modal ──────────────────────────────────────────── */}
        <CfoApproveModal
          open={showCfoModal}
          form={cfoForm}
          onFormChange={setCfoForm}
          onCancel={() => setShowCfoModal(false)}
          onConfirm={handleCfoApprove}
          loading={cfoApproving}
        />

        {/* Iter215 · CFO Update (IA diff) Modal */}
        <CfoUpdateModal
          open={showCfoUpdateModal}
          offerId={id}
          onClose={() => setShowCfoUpdateModal(false)}
          onSent={() => { load(); }}
        />

        {/* ── Approval History ────────────────────────────────────────────── */}
        <ApprovalHistory entries={offer.approval_history} />

        {/* ── Silent Admin Modal (solo dredondo) ─────────────────────────── */}
        <SilentAdminModal
          open={showSilentModal}
          offerTitle={offer.title}
          onCancel={() => setShowSilentModal(false)}
          onConfirm={handleSilentAdminApprove}
          loading={silentApproving}
        />
      </div>

      {/* ── AI Analysis Section ─────────────────────────────────────────────── */}
      <OfferAISummary
        aiAnalysis={aiAnalysis}
        analyzingAi={analyzingAi}
        runAiAnalysis={runAiAnalysis}
        handlePrintReport={handlePrintReport}
        aiModel={aiModel}
        setAiModel={setAiModel}
        aiModelsCatalog={aiModelsCatalog}
        includeInternalHours={includeInternalHours}
        setIncludeInternalHours={setIncludeInternalHours}
      />

      {/* ── CAPEX/OPEX Tables ────────────────────────────────────────────────── */}
      <div className="space-y-3" data-testid="cost-sections">
        {/* Gráfico plurianual de coste mantenimiento tecnología (si la oferta lo tiene) */}
        <MaintenanceYearlyChart data={buildMaintenanceYearlyData(offer)} />
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-gray-900 font-['Outfit']">Desglose CAPEX / OPEX</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowExcelImport(true); setExcelImportFile(null); setImportResult(null); }}
              className="flex items-center gap-1.5 text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 font-medium transition-all"
              data-testid="import-excel-offer-btn"
            >
              <FileSpreadsheet size={12} /> Importar Excel
            </button>
            <button onClick={() => addSection("OPEX")} className="flex items-center gap-1.5 text-xs border border-teal-300 text-teal-700 px-3 py-1.5 rounded-lg hover:bg-teal-50 font-medium" data-testid="add-opex-section-btn">
              <Plus size={12} /> OPEX
            </button>
            <button onClick={() => addSection("CAPEX")} className="flex items-center gap-1.5 text-xs border border-[#007934] text-[#007934] px-3 py-1.5 rounded-lg hover:bg-[#E8F5E9] font-medium" data-testid="add-capex-section-btn">
              <Plus size={12} /> CAPEX
            </button>
          </div>
        </div>

        {sections.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200 text-gray-400">
            <p className="text-sm">Añade secciones de CAPEX y/u OPEX para desglosar los costes TIC</p>
          </div>
        )}

        {sections.map((sec) => {
          const style = CATEG_STYLE[sec.category] || CATEG_STYLE.CAPEX;
          const baseTotal = sectionBaseTotal(sec);
          const total = sectionTotal(sec);
          const years = sec.category === "CAPEX" ? Math.max(sec.years || 1, 1) : 1;
          const isExpanded = expandedSections[sec.id] !== false;
          return (
            <div key={sec.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`cost-section-${sec.id}`}>
              <div className={`flex items-center justify-between px-4 py-3 cursor-pointer ${style.bg} ${style.text}`}
                onClick={() => setExpandedSections(prev => ({ ...prev, [sec.id]: !isExpanded }))}>
                <div className="flex items-center gap-3 flex-1">
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${style.badge}`}>{sec.category}</span>
                    {(sec.subcategory === "Horas Internas" || sec.subcategory === "Puesta en Marcha") && (
                      <span
                        data-testid={`internal-hours-badge-${sec.id}`}
                        title={sec.subcategory === "Puesta en Marcha"
                          ? "Esfuerzo interno Valoriza para puesta en marcha (one-shot)"
                          : "Horas internas tecnología recurrentes (mantenimiento cruise)"}
                        className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 bg-indigo-100 text-indigo-700 flex items-center gap-1"
                      >
                        ⏱ Horas internas
                      </span>
                    )}
                    <input value={sec.subcategory} onClick={e => e.stopPropagation()}
                      onChange={e => updateSection(sec.id, "subcategory", e.target.value)}
                      onBlur={saveSection}
                      className="bg-transparent border-none outline-none text-xs font-medium w-20 flex-shrink-0 opacity-80"
                      placeholder="Subcategoría" />
                    <span className="opacity-40 flex-shrink-0">·</span>
                    <input value={sec.section_name} onClick={e => e.stopPropagation()}
                      onChange={e => updateSection(sec.id, "section_name", e.target.value)}
                      onBlur={saveSection}
                      className="bg-transparent border-none outline-none text-sm font-semibold flex-1 min-w-0"
                      placeholder="Nombre de la sección" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-sm">{fmt(total)} €</span>
                  {sec.category === "CAPEX" && (
                    <div className="flex items-center gap-1 bg-black/20 rounded-lg px-2 py-1" onClick={e => e.stopPropagation()}>
                      <span className="text-[10px] opacity-70">×</span>
                      <input
                        type="number" min="1" max="30"
                        value={sec.years || 1}
                        onChange={e => updateSection(sec.id, "years", Math.max(1, parseInt(e.target.value) || 1))}
                        onBlur={saveSection}
                        className="bg-transparent border-none outline-none text-xs font-bold w-7 text-center"
                        title="Años de amortización CAPEX"
                      />
                      <span className="text-[10px] opacity-70">año{(sec.years || 1) !== 1 ? "s" : ""}</span>
                    </div>
                  )}
                  <button onClick={e => { e.stopPropagation(); removeSection(sec.id); }} className="opacity-60 hover:opacity-100 ml-1"><Trash2 size={13} /></button>
                </div>
              </div>
              {isExpanded && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase border-b border-gray-50 bg-gray-50/50">
                        <th className="text-left px-4 py-2.5 font-medium w-2/5">Concepto</th>
                        <th className="text-right px-3 py-2.5 font-medium w-16">Cantidad</th>
                        <th className="text-right px-3 py-2.5 font-medium w-28">€/unidad</th>
                        <th className="text-right px-3 py-2.5 font-medium w-24">Total</th>
                        <th className="text-left px-3 py-2.5 font-medium">Nota</th>
                        <th className="px-2 py-2.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(sec.items || []).map(item => {
                        const itemTotal = (item.cantidad || 0) * (item.precio_unitario || 0);
                        return (
                          <tr key={item.id} className="hover:bg-gray-50/70 group">
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <input value={item.concepto} onChange={e => updateItem(sec.id, item.id, "concepto", e.target.value)}
                                  onBlur={saveSection}
                                  className="w-full text-sm text-gray-900 border-none outline-none bg-transparent focus:bg-white focus:border-b focus:border-[#007934] placeholder-gray-300"
                                  placeholder="Descripción del concepto" />
                                {item.alerta && (
                                  <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid={`alert-badge-${item.id}`}>
                                    <AlertCircle size={10} /> ALERTA
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <input type="number" min="0" value={item.cantidad} onChange={e => updateItem(sec.id, item.id, "cantidad", e.target.value)}
                                onBlur={saveSection}
                                className="w-full text-sm text-right border-none outline-none bg-transparent focus:bg-white focus:border-b focus:border-[#007934] text-gray-700" />
                            </td>
                            <td className="px-3 py-2">
                              <input type="number" min="0" step="0.01" value={item.precio_unitario} onChange={e => updateItem(sec.id, item.id, "precio_unitario", e.target.value)}
                                onBlur={saveSection}
                                className="w-full text-sm text-right border-none outline-none bg-transparent focus:bg-white focus:border-b focus:border-[#007934] text-gray-700" />
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900 text-sm">{fmt(itemTotal)} €</td>
                            <td className="px-3 py-2">
                              <input value={item.nota || ""} onChange={e => updateItem(sec.id, item.id, "nota", e.target.value)}
                                onBlur={saveSection}
                                className="w-full text-xs text-gray-400 border-none outline-none bg-transparent focus:bg-white focus:border-b focus:border-gray-300 placeholder-gray-200"
                                placeholder="Nota opcional" />
                            </td>
                            <td className="px-2 py-2">
                              <button onClick={() => removeItem(sec.id, item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all p-0.5">
                                <X size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="px-4 py-2.5">
                          <button onClick={() => { addItem(sec.id); setExpandedSections(prev => ({...prev, [sec.id]: true})); }}
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#007934] transition-colors" data-testid={`add-item-${sec.id}`}>
                            <Plus size={12} /> Añadir concepto
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {sec.category === "CAPEX" && years > 1 ? (
                            <div className="text-right">
                              <p className="text-xs text-gray-400">{fmt(baseTotal)} € × {years} años</p>
                              <p className={`font-bold text-sm text-[#007934]`}>{fmt(total)} €</p>
                            </div>
                          ) : (
                            <span className={`font-bold text-sm ${sec.category === "OPEX" ? "text-teal-700" : "text-[#007934]"}`}>{fmt(total)} €</span>
                          )}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {sections.length > 0 && (
          <div className="bg-gray-900 text-white rounded-xl p-4 flex items-center justify-between" data-testid="grand-total-bar">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider">OPEX / año{opexIsMultiYear ? " (promedio)" : ""}</p>
                <p className="font-bold text-teal-400">{fmt(opexAnnualLabel)} €</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider">CAPEX</p>
                <p className="font-bold text-green-400">{fmt(capexTotal)} €</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">TOTAL TIC</p>
              <p className="text-2xl font-bold font-['Outfit']">{fmt(grandTotal)} €</p>
            </div>
          </div>
        )}
      </div>

      <OfferImprovements offerId={id} />

      {/* ── Excel Import Modal ──────────────────────────────────────────────── */}
      {showExcelImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="excel-import-offer-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-[#007934]" />
                <h2 className="text-lg font-bold font-['Outfit']">Importar Excel → CAPEX/OPEX</h2>
              </div>
              <button onClick={() => setShowExcelImport(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {!importResult ? (
                <>
                  <p className="text-sm text-gray-500">La Pando analizará el Excel e <strong>reemplazará completamente</strong> las secciones CAPEX/OPEX actuales con las partidas detectadas.</p>
                  <div
                    onClick={() => excelInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      excelImportFile ? "border-[#007934] bg-[#E8F5E9]" : "border-gray-200 hover:border-[#007934] hover:bg-gray-50"
                    }`}
                  >
                    <input ref={excelInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                      onChange={e => setExcelImportFile(e.target.files[0] || null)} />
                    {excelImportFile ? (
                      <div className="flex items-center justify-center gap-2 text-[#007934]">
                        <FileSpreadsheet size={18} />
                        <span className="text-sm font-semibold">{excelImportFile.name}</span>
                        <button onClick={e => { e.stopPropagation(); setExcelImportFile(null); }} className="ml-2 text-gray-400 hover:text-red-500">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload size={24} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm font-medium text-gray-500">Selecciona el Excel del Alcance IT o pliego</p>
                        <p className="text-xs text-gray-400 mt-1">.xlsx, .xls — la Pando detecta el formato automáticamente</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowExcelImport(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
                    <button
                      onClick={handleExcelImportInOffer}
                      disabled={!excelImportFile || importingExcel}
                      className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="confirm-excel-import-offer-btn"
                    >
                      {importingExcel ? <><Loader2 size={14} className="animate-spin" /> Analizando...</> : <><Brain size={14} /> Importar con Pando</>}
                    </button>
                  </div>
                  {importingExcel && <p className="text-xs text-gray-400 text-center">Procesando con Pando... 20-40 segundos</p>}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-[#E8F5E9] border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle size={16} className="text-[#007934]" />
                      <p className="font-semibold text-[#007934] text-sm">{importResult.sections_count} secciones importadas</p>
                    </div>
                    <p className="text-sm text-gray-700">{importResult.resumen}</p>
                  </div>
                  <button onClick={() => setShowExcelImport(false)} className="w-full bg-[#007934] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#006528]">
                    Ver partidas importadas
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Iter223 · Modal para vincular la oferta con una Licitación */}
      <LinkLicitacionModal
        open={showLinkModal}
        offer={offer}
        onClose={() => setShowLinkModal(false)}
        onLinked={() => { setShowLinkModal(false); load(); }}
      />
    </div>
  );
}
